import { GlobalSettings } from "../global_settings.js";
import { TelegramAPISettings } from "../telegram_api_settings.js";
import { VersionLoader } from "../version_loader.js";
import { AnalyticsService } from "./analytics_service.js";
import { AnalyticsContext, AnalyticsEventName, AnalyticsEventNameLike, AnalyticsEventProperties, AnalyticsInitOptions, AnalyticsPropertyKey } from "./analytics_types.js";
import { getCommonAnalyticsFields } from "./analytics_identity.js";
import { AthenaProxyAnalyticsTransport } from "./transports/athena_proxy_transport.js";
import { ConsoleAnalyticsMethod, ConsoleAnalyticsTransport } from "./transports/console_analytics_transport.js";
import { FanoutAnalyticsTransport } from "./transports/fanout_analytics_transport.js";
// import { GoogleSignInPlugin } from "../plugins/play_games_plugin.js";
import { AnalyticStore } from "./analytic_store.js";
import { UtilityPlugin } from "../plugins/utility_plugin.js";

export const analytics = new AnalyticsService( new ConsoleAnalyticsTransport( ConsoleAnalyticsMethod.INFO ) );
const GA_MEASUREMENT_ID = 'G-0KZ39EL21W';
let ga_transport_attached = false;
let web_session_finish_tracking_attached = false;
let web_session_finish_tracked = false;
let monetization_segment_tracking_attached = false;

const safeLocalStorageGet = ( key: string ): string | null =>
{
  try
  {
    return localStorage.getItem( key );
  }
  catch
  {
    return null;
  }
};

const parseBool = ( v: string | null ): boolean | undefined =>
{
  if ( v == null ) return undefined;
  const s = v.trim().toLowerCase();
  if ( s === '1' || s === 'true' || s === 'yes' || s === 'on' ) return true;
  if ( s === '0' || s === 'false' || s === 'no' || s === 'off' ) return false;
  return undefined;
};

/*
const normalizeVersion = ( v: unknown ): string =>
{
  const raw = String( v ?? '' ).trim();
  if ( raw.length === 0 ) return '0_0_0';
  if ( /^\d{4}-\d{2}-\d{2}t/i.test( raw ) ) return '0_0_0';
  if ( /^\d{4}-\d{2}-\d{2}$/.test( raw ) ) return '0_0_0';
  const underscored = raw.replace( /[^a-zA-Z0-9]+/g, '_' ).replace( /^_+|_+$/g, '' );
  if ( underscored.length === 0 ) return '0_0_0';
  return underscored;
};
*/

const isNativeCapacitorPlatform = (): boolean =>
{
  try
  {
    const platform = window.Capacitor?.getPlatform?.();
    return platform === 'android' || platform === 'ios';
  }
  catch
  {
    return false;
  }
};

const isWebRuntime = (): boolean => !isNativeCapacitorPlatform();

const attachGoogleAnalyticsTransportIfNeeded = (): void =>
{
  if ( ga_transport_attached || !isWebRuntime() ) return;

  const current_transport = analytics.getTransport();

  analytics.setTransport( new FanoutAnalyticsTransport( [
    current_transport,
  ] ) );

  ga_transport_attached = true;
};

const trackWebSessionFinish = ( source: string ): void =>
{
  if ( web_session_finish_tracked || !isWebRuntime() ) return;
  web_session_finish_tracked = true;

  const duration_seconds = Math.floor( ( Date.now() - session_start_time ) / 1000 );
  analytics.track( AnalyticsEventName.SESSION_FINISH, {
    [ AnalyticsPropertyKey.TIMESTAMP ]: new Date().toISOString(),
    [ AnalyticsPropertyKey.DURATION ]: duration_seconds,
  } );
};

const attachWebSessionFinishTrackingIfNeeded = (): void =>
{
  if ( web_session_finish_tracking_attached || !isWebRuntime() ) return;
  web_session_finish_tracking_attached = true;

  window.addEventListener( 'pagehide', ( event: PageTransitionEvent ) =>
  {
    if ( event.persisted ) return;
    trackWebSessionFinish( 'pagehide' );
  }, { capture: true } );

  window.addEventListener( 'beforeunload', () =>
  {
    trackWebSessionFinish( 'beforeunload' );
  }, { capture: true } );
};

const attachMonetizationSegmentTracking = (): void =>
{
  if ( monetization_segment_tracking_attached ) return;
  monetization_segment_tracking_attached = true;

  window.addEventListener( 'offersEntitlementsChanged', ( event: Event ) =>
  {
    const detail = ( event as CustomEvent<unknown> ).detail;
    const segment = detail && typeof detail === 'object'
      ? ( detail as Record<string, unknown> ).monetization_segment
      : undefined;
    if ( segment !== 'free' && segment !== 'paid' ) return;

    analytics.setCommonProperties( {
      [AnalyticsPropertyKey.MONETIZATION_SEGMENT]: segment,
    } );
  } );
};

const initAthenaTransportIfEnabled = ( endpoint_url?: string /*login_payload?: Record<string, unknown>*/ ): AthenaProxyAnalyticsTransport | null =>
{
  const enabled_override = parseBool( safeLocalStorageGet( 'athena_analytics_enabled' ) );
  const enabled = enabled_override ?? true;
  if ( !enabled ) return null;

  const include_raw_event = parseBool( safeLocalStorageGet( 'athena_analytics_include_raw_event' ) ) ?? false;
  const debug = parseBool( safeLocalStorageGet( 'athena_analytics_debug' ) ) ?? !GlobalSettings.IS_PROD;
  const is_qa_override = parseBool( safeLocalStorageGet( 'athena_analytics_is_qa' ) );

  try
  {
    if ( debug )
    {
      console.info( '[ATHENA_ANALYTICS] init' );
    }
  } catch {}

  const athena_transport = new AthenaProxyAnalyticsTransport( {
    endpoint_url: safeLocalStorageGet( 'athena_analytics_endpoint_url' ) ?? endpoint_url ?? GlobalSettings.ATHENA_ANALYTICS_BASE_URL,
    timeout_ms: 5000,
    include_raw_event,
    debug,
    is_qa_override,
  } );
  const server_session_id = AnalyticStore.getSessionIdFromServer();
  if ( server_session_id ) athena_transport.setSessionId( server_session_id );

  if ( GlobalSettings.IS_PROD )
  {
    analytics.setTransport( athena_transport );
    return athena_transport;
  }

  analytics.setTransport( new FanoutAnalyticsTransport( [
    new ConsoleAnalyticsTransport( ConsoleAnalyticsMethod.INFO ),
    athena_transport,
  ] ) );
  return athena_transport;
};

const getPlatform = (): string =>
{
  try
  {
    const cap_platform = window.Capacitor?.getPlatform?.();
    if ( cap_platform ) return String( cap_platform );
  } catch {}

  try
  {
    const tg_platform = window.Telegram?.WebApp?.platform;
    if ( tg_platform ) return `telegram:${String( tg_platform )}`;
  } catch {}

  return 'web';
};

const buildDefaultContext = (): AnalyticsContext =>
{
  return {
    app: 'royalsmash',
    app_version: VersionLoader.getVersion(),
    is_prod: GlobalSettings.IS_PROD,
    platform: getPlatform(),
    locale: TelegramAPISettings.getUserLanguage() ?? ( navigator.language || null ) ?? undefined,
    timezone_offset_minutes: new Date().getTimezoneOffset(),
    url: safeUrl(),
    user_agent: navigator.userAgent,
    screen: { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio || 1 }
  };
};

export const resolveAndroidDeviceId = async (): Promise<string | undefined> =>
{
  // try
  // {
  //   if ( getPlatform() !== 'android' ) return undefined;
  //   const android_id = await GoogleSignInPlugin.getAndroidId();
  //   return android_id ?? undefined;
  // }
  // catch
  // {
  //   return undefined;
  // }

  return undefined;
};

const safeUrl = (): string =>
{
  try
  {
    return window.location.href;
  } catch
  {
    return '';
  }
};

let athena_transport_instance: AthenaProxyAnalyticsTransport | null = null;
let session_start_time = Date.now();

export const getAthenaTransport = (): AthenaProxyAnalyticsTransport | null => athena_transport_instance;

export const initAnalytics = async ( options: AnalyticsInitOptions = {} ): Promise<void> =>
{
  try
  {
    await VersionLoader.load();
  } catch {}

  const app_start_info = await UtilityPlugin.getAppStartInfo();
  const default_context = buildDefaultContext();
  const android_device_id = app_start_info?.deviceId ?? await resolveAndroidDeviceId();
  if ( android_device_id )
  {
    default_context.device_id = android_device_id;
  }
  const common_fields = getCommonAnalyticsFields();

  AnalyticStore.init( common_fields.userid );
  if ( app_start_info?.sessionId )
  {
    AnalyticStore.setSessionId( app_start_info.sessionId );
  }

  analytics.init( {
    ...options,
    enabled: options.enabled ?? true,
    context: { ...default_context, ...( options.context ?? {} ) },
    user_id: AnalyticStore.getUserId(),
    session_id: options.session_id ?? app_start_info?.sessionId ?? AnalyticStore.getSessionId(),
    common_properties: {
      ...( options.common_properties ?? {} ),
      [ AnalyticsPropertyKey.USERID ]: common_fields.userid,
      [ AnalyticsPropertyKey.SIGNUPTIMESTAMP ]: common_fields.signuptimestamp,
      [ AnalyticsPropertyKey.PLATFORM ]: common_fields.platform,
      [ AnalyticsPropertyKey.COUNTRY ]: common_fields.country,
      [ AnalyticsPropertyKey.MONETIZATION_SEGMENT ]: 'unknown',
      [ AnalyticsPropertyKey.CAMPAIGN ]: 'unknown',
    }
  } );

  try
  {
    console.info( '[ANALYTICS] userId', analytics.getUserId() );
  } catch {}

  const now_iso = new Date().toISOString();

/*  
  const is_qa = typeof default_context.is_prod === 'boolean' ? !default_context.is_prod : false;
  const launchedFromPush = !!globalThis.__launchedFromPush;
  const sourceid = launchedFromPush ? 'push' : 'icon';

  const login_payload: Record<string, unknown> = {
    platform: common_fields.platform,
    version: normalizeVersion( default_context.app_version ),
    gamelocale: String( default_context.locale ?? '' ),
    isqa: is_qa,
    device: String( default_context.device_id ?? '' ),
    signuptimestamp: common_fields.signuptimestamp,
    userlevel: common_fields.userlevel,
    sourceid,
  };
*/

  let athena_transport: AthenaProxyAnalyticsTransport | null = null;
  try
  {
    athena_transport = initAthenaTransportIfEnabled( app_start_info?.athenaApiUrl /*login_payload*/ );
    athena_transport_instance = athena_transport;
  }
  catch ( e )
  {
    try
    {
      console.warn( '[ANALYTICS] Athena transport init failed', e );
    } catch {}
  }

  attachGoogleAnalyticsTransportIfNeeded();
  attachWebSessionFinishTrackingIfNeeded();
  attachMonetizationSegmentTracking();

  const startup = async (): Promise<void> =>
  {
/*
    if ( athena_transport )
    {
      try
      {
        await athena_transport.preLogin( common_fields.userid, login_payload );
      } catch {}
    }
*/
    if ( common_fields.is_first_open )
    {
      analytics.track( AnalyticsEventName.FIRST_OPEN_AFTER_INSTALL, {
        [ AnalyticsPropertyKey.TIMESTAMP ]: now_iso,
      } );
    }

    session_start_time = Date.now();
    web_session_finish_tracked = false;
    
  };

  void startup();

  globalThis.analytics = analytics;

  analytics.setContext( { app_version: VersionLoader.getVersion() } );
};

export const trackWebEvent = ( name: AnalyticsEventNameLike, properties?: AnalyticsEventProperties ): void =>
{
  if ( !isWebRuntime() ) return;
  analytics.track( name, properties );
};

export { AnalyticsEventName };
export { AnalyticsPropertyKey } from "./analytics_types.js";
export { AnalyticsAdNetwork } from "./analytics_types.js";
